(() => {
  const configNode = document.querySelector('[data-cart-drawer-config]');
  const drawer = document.querySelector('[data-cart-drawer]');
  if (!configNode || !drawer) return;

  let config;
  try { config = JSON.parse(configNode.textContent); } catch { return; }

  const panel = drawer.querySelector('.cart-drawer__panel');
  const title = drawer.querySelector('[data-cart-drawer-title]');
  const body = drawer.querySelector('[data-cart-drawer-body]');
  const footer = drawer.querySelector('[data-cart-drawer-footer]');
  const summary = drawer.querySelector('[data-cart-drawer-summary]');
  const subtotal = drawer.querySelector('[data-cart-drawer-subtotal]');
  const originalSubtotalRow = drawer.querySelector('[data-cart-original-subtotal-row]');
  const originalSubtotal = drawer.querySelector('[data-cart-original-subtotal]');
  const discountsContainer = drawer.querySelector('[data-cart-discounts]');
  const shippingBar = drawer.querySelector('[data-cart-shipping-bar]');
  const notice = drawer.querySelector('[data-cart-notice]');
  const recommendations = drawer.querySelector('[data-cart-recommendations]');
  const error = drawer.querySelector('[data-cart-drawer-error]');
  let cart = null;
  let lastTrigger = null;
  let inFlight = false;

  const money = cents => {
    const value = Number(cents || 0);
    const format = config.moneyFormat || '¥{{amount_no_decimals}}';
    const comma = number => number.toLocaleString('en-US');
    const decimal = (value / 100).toFixed(2);
    const noDecimals = Math.round(value / 100);
    return format
      .replace(/\{\{amount_no_decimals\}\}/g, comma(noDecimals))
      .replace(/\{\{amount\}\}/g, comma(Number(decimal)))
      .replace(/\{\{amount_with_comma_separator\}\}/g, decimal.replace('.', ','))
      .replace(/\{\{amount_no_decimals_with_comma_separator\}\}/g, String(noDecimals).replace(/\B(?=(\d{3})+(?!\d))/g, '.'));
  };

  const escapeHtml = value => String(value ?? '').replace(/[&<>'"]/g, character => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', "'": '&#39;', '"': '&quot;' }[character]));

  const updateCartCount = count => {
    document.querySelectorAll('[data-cart-count]').forEach(node => { node.textContent = String(count); });
    document.querySelectorAll('[data-cart-drawer-trigger]').forEach(trigger => {
      const label = trigger.dataset.cartLabel || config.cartLabel;
      trigger.setAttribute('aria-label', `${label} (${count})`);
    });
  };

  const setError = message => {
    if (!error) return;
    error.textContent = message || '';
    error.hidden = !message;
  };

  const variantLabel = item => item.variant_title && item.variant_title !== 'Default Title'
    ? `<p class="cart-drawer__variant">${escapeHtml(item.variant_title)}</p>`
    : '';

  const itemMarkup = item => {
    const image = item.featured_image?.url || item.image || '';
    const imageMarkup = image
      ? `<img src="${escapeHtml(image)}" alt="${escapeHtml(item.product_title || item.title)}" width="160" height="160" loading="lazy">`
      : '<span class="cart-drawer__image-placeholder" aria-hidden="true"></span>';
    const itemDiscounts = (item.discounts && item.discounts.length > 0)
      ? item.discounts
      : (item.line_level_discount_allocations || []).map(a => ({
          title: a.discount_application?.title || '割引',
          amount: a.amount
        }));
    const totalItemDiscount = itemDiscounts.reduce((sum, d) => sum + Number(d.amount || 0), 0);
    const originalLinePrice = Number(item.original_line_price || item.line_price || 0);
    const finalLinePrice = (totalItemDiscount > 0 && originalLinePrice > totalItemDiscount)
      ? (originalLinePrice - totalItemDiscount)
      : (Number(item.line_price || item.final_line_price || originalLinePrice) < originalLinePrice
          ? Number(item.line_price)
          : originalLinePrice);
    const hasDiscount = originalLinePrice > finalLinePrice || totalItemDiscount > 0;
    const itemDiscountsMarkup = itemDiscounts.length
      ? `<div class="cart-drawer__item-discounts">${itemDiscounts.map(d => `<span class="cart-drawer__item-discount"><span class="cart-drawer__discount-tag">割引</span> ${escapeHtml(d.title)}: -${money(d.amount)}</span>`).join('')}</div>`
      : '';
    return `<article class="cart-drawer__item" data-cart-item-key="${escapeHtml(item.key)}">
      <a class="cart-drawer__image" href="${escapeHtml(item.url)}">${imageMarkup}</a>
      <div class="cart-drawer__item-info">
        <a class="cart-drawer__item-title" href="${escapeHtml(item.url)}">${escapeHtml(item.product_title || item.title)}</a>
        ${variantLabel(item)}
        <p class="cart-drawer__item-price">${money(finalLinePrice)}${hasDiscount ? ` <s>${money(originalLinePrice)}</s>` : ''}</p>
        ${itemDiscountsMarkup}
        <div class="cart-drawer__item-actions">
          <div class="cart-drawer__quantity" aria-label="数量">
            <button type="button" data-cart-quantity="${item.quantity - 1}" aria-label="数量を減らす">−</button>
            <span aria-live="polite">${item.quantity}</span>
            <button type="button" data-cart-quantity="${item.quantity + 1}" aria-label="数量を増やす">＋</button>
          </div>
          <button class="cart-drawer__remove" type="button" data-cart-remove>削除</button>
        </div>
      </div>
    </article>`;
  };

  const normalizeProduct = product => ({
    id: Number(product.id),
    title: product.title,
    url: product.url,
    available: product.available,
    price: product.price,
    image: product.featured_image?.url || product.featured_image || product.image || null,
    variants: product.variants || []
  });

  const recommendationMarkup = product => {
    const variants = product.variants.filter(variant => variant.available);
    const isSingleVariant = variants.length === 1 && variants[0].title === 'Default Title';
    const image = product.image
      ? `<img src="${escapeHtml(product.image)}" alt="${escapeHtml(product.title)}" width="220" height="220" loading="lazy">`
      : '<span class="cart-drawer__recommendation-placeholder" aria-hidden="true"></span>';
    const action = isSingleVariant
      ? `<button type="button" data-cart-recommendation-add="${escapeHtml(variants[0].id)}">＋ 追加</button>`
      : `<a href="${escapeHtml(product.url)}">商品を見る</a>`;
    return `<article class="cart-drawer__recommendation">
      <a class="cart-drawer__recommendation-image" href="${escapeHtml(product.url)}">${image}</a>
      <a class="cart-drawer__recommendation-title" href="${escapeHtml(product.url)}">${escapeHtml(product.title)}</a>
      <p>${money(product.price)}</p>
      ${action}
    </article>`;
  };

  const renderRecommendations = products => {
    if (!recommendations) return;
    recommendations.replaceChildren();
    recommendations.classList.remove('is-collapsed');
    recommendations.hidden = products.length === 0;
    if (!products.length) return;
    recommendations.innerHTML = `<div class="cart-drawer__recommendations-header">
      <h3>おすすめの作品</h3>
      <button class="cart-drawer__recommendations-toggle" type="button" data-cart-recommendations-toggle aria-expanded="true" aria-label="おすすめの作品を閉じる">−</button>
    </div><div class="cart-drawer__recommendation-grid">${products.map(recommendationMarkup).join('')}</div>`;
  };

  const loadRecommendations = async currentCart => {
    const reference = currentCart.items.find(item => item.product_id);
    if (!reference) return;
    const excludedIds = new Set(currentCart.items.map(item => Number(item.product_id)));
    const selectProducts = products => products.map(normalizeProduct).filter(product => (
      product.available && !excludedIds.has(product.id) && product.id !== Number(reference.product_id)
    )).slice(0, 5);
    let products = [];
    try {
      const endpoint = new URL('recommendations/products.json', new URL(config.rootUrl, window.location.origin)).toString();
      const response = await fetch(`${endpoint}?product_id=${encodeURIComponent(reference.product_id)}&limit=8&intent=related`, { headers: { Accept: 'application/json' }, credentials: 'same-origin' });
      if (response.ok) products = selectProducts((await response.json()).products || []);
    } catch {}
    if (products.length < 5) {
      const fallback = selectProducts(config.fallbackProducts || []);
      const selectedIds = new Set(products.map(product => product.id));
      products = [...products, ...fallback.filter(product => !selectedIds.has(product.id))].slice(0, 5);
    }
    renderRecommendations(products);
  };

  const render = nextCart => {
    cart = nextCart;
    const isKo = document.documentElement.lang === 'ko';
    const count = cart?.item_count || 0;
    title.textContent = isKo ? `장바구니（${count}）` : `カート（${count}）`;
    updateCartCount(count);
    setError('');
    if (!count) {
      body.innerHTML = isKo
        ? `<div class="cart-drawer__empty"><p>장바구니가 비어 있습니다.</p><a href="${escapeHtml(config.collectionsUrl)}">작품 보기 <span>→</span></a></div>`
        : `<div class="cart-drawer__empty"><p>カートは空です。</p><a href="${escapeHtml(config.collectionsUrl)}">作品を見る <span>→</span></a></div>`;
      summary.hidden = true;
      if (shippingBar) shippingBar.hidden = true;
      renderRecommendations([]);
      return;
    }
    body.innerHTML = `<div class="cart-drawer__items">${cart.items.map(itemMarkup).join('')}</div>`;
    summary.hidden = false;
    renderRecommendations([]);

    // 1. Free Shipping Bar
    const thresholdCents = Number(config.freeShippingThreshold || 10000) * 100;
    const currentTotal = Number(cart.total_price || 0);
    const isFreeShipping = Boolean(config.freeShippingEnabled && currentTotal >= thresholdCents);
    if (shippingBar) {
      if (!config.freeShippingEnabled || !config.freeShippingThreshold) {
        shippingBar.hidden = true;
      } else {
        shippingBar.hidden = false;
        if (isFreeShipping) {
          shippingBar.innerHTML = `<p class="cart-drawer__shipping-message is-free" data-cart-shipping-message>${isKo ? '무료 배송이 적용됩니다' : '送料無料が適用されます'}</p>`;
        } else {
          const remaining = thresholdCents - currentTotal;
          shippingBar.innerHTML = `<p class="cart-drawer__shipping-message" data-cart-shipping-message>${isKo ? `무료 배송까지 <strong data-cart-shipping-remaining>${money(remaining)}</strong> 남음` : `送料無料まであと <strong data-cart-shipping-remaining>${money(remaining)}</strong>`}</p>`;
        }
      }
    }

    // 2. Summary Discounts
    const discountLines = [];
    if (cart.cart_level_discount_applications && cart.cart_level_discount_applications.length > 0) {
      cart.cart_level_discount_applications.forEach(app => {
        discountLines.push({
          title: app.title || (isKo ? '할인' : '割引'),
          amount: Number(app.total_allocated_amount || 0)
        });
      });
    } else {
      cart.items.forEach(item => {
        const lineDiscounts = (item.discounts && item.discounts.length > 0)
          ? item.discounts
          : (item.line_level_discount_allocations || []).map(a => ({ title: a.discount_application?.title || (isKo ? '할인' : '割引'), amount: a.amount }));
        lineDiscounts.forEach(ld => {
          const existing = discountLines.find(d => d.title === ld.title);
          if (existing) {
            existing.amount += Number(ld.amount || 0);
          } else {
            discountLines.push({ title: ld.title, amount: Number(ld.amount || 0) });
          }
        });
      });
    }

    if (discountLines.length === 0 && Number(cart.total_discount || 0) > 0) {
      discountLines.push({ title: (isKo ? '할인' : '割引'), amount: Number(cart.total_discount || 0) });
    }

    const hasDiscount = Number(cart.total_discount || 0) > 0 || discountLines.length > 0;
    if (originalSubtotalRow) {
      originalSubtotalRow.hidden = !hasDiscount;
      if (originalSubtotal) originalSubtotal.textContent = money(cart.original_total_price);
    }

    if (discountsContainer) {
      if (discountLines.length > 0) {
        if (cart.cart_level_discount_applications && cart.cart_level_discount_applications.length > 0) {
          discountsContainer.innerHTML = discountLines.map(line => `
            <div class="cart-drawer__discount-line">
              <span class="cart-drawer__discount-title"><span class="cart-drawer__discount-tag">${isKo ? '할인' : '割引'}</span> ${escapeHtml(line.title)}</span>
              <span class="cart-drawer__discount-amount">-${money(line.amount)}</span>
            </div>
          `).join('');
        } else {
          discountsContainer.innerHTML = `
            <div class="cart-drawer__discount-line">
              <span class="cart-drawer__discount-title"><span class="cart-drawer__discount-tag">${isKo ? '할인' : '割引'}</span> ${isKo ? '할인 적용' : '割引適用'}</span>
              <span class="cart-drawer__discount-amount">-${money(cart.total_discount)}</span>
            </div>
          `;
        }
      } else {
        discountsContainer.innerHTML = '';
      }
    }
    subtotal.textContent = money(cart.total_price);

    // 3. Dynamic notice
    if (notice) {
      if (isKo) {
        notice.innerHTML = isFreeShipping
          ? '무료 배송이 적용됩니다.<br>세금 등은 결제 시 확인하실 수 있습니다.'
          : '배송비는 결제 시 계산됩니다.<br>일정 금액 이상 구매 시 무료 배송입니다.';
      } else {
        notice.innerHTML = isFreeShipping
          ? '送料無料が適用されます。<br>税金等は購入手続き時に確認できます。'
          : '送料は購入手続き時に計算されます。<br>対象金額以上のご注文は送料無料です。';
      }
    }
  };

  const refresh = async () => {
    const response = await fetch(`${config.cartUrl}.js`, { headers: { Accept: 'application/json' }, credentials: 'same-origin' });
    if (!response.ok) throw new Error('cart-fetch-failed');
    const nextCart = await response.json();
    render(nextCart);
    if (nextCart.item_count) await loadRecommendations(nextCart);
    return nextCart;
  };

  const setBusy = busy => {
    inFlight = busy;
    drawer.classList.toggle('is-busy', busy);
    drawer.querySelectorAll('[data-cart-quantity], [data-cart-remove]').forEach(control => { control.disabled = busy; });
  };

  const open = async trigger => {
    lastTrigger = trigger || document.activeElement;
    drawer.hidden = false;
    drawer.setAttribute('aria-hidden', 'false');
    document.body.classList.add('cart-drawer-open');
    document.querySelectorAll('[data-cart-drawer-trigger]').forEach(button => button.setAttribute('aria-expanded', 'true'));
    try { await refresh(); } catch { setError('カートを読み込めませんでした。'); }
    panel.focus();
  };

  const close = () => {
    drawer.hidden = true;
    drawer.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('cart-drawer-open');
    document.querySelectorAll('[data-cart-drawer-trigger]').forEach(button => button.setAttribute('aria-expanded', 'false'));
    lastTrigger?.focus?.();
  };

  const change = async (key, quantity) => {
    if (inFlight) return;
    setBusy(true);
    setError('');
    try {
      const response = await fetch(config.cartChangeUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({ id: key, quantity: Math.max(0, Number(quantity) || 0) })
      });
      if (!response.ok) throw new Error('cart-change-failed');
      await refresh();
    } catch {
      setError('カートを更新できませんでした。');
    } finally {
      setBusy(false);
    }
  };

  const add = async form => {
    if (inFlight) return;
    const button = form.querySelector('[data-add-to-cart]');
    const originalText = button?.textContent;
    setBusy(true);
    if (button) { button.disabled = true; button.textContent = '追加中…'; }
    form.querySelector('[data-product-form-error]')?.setAttribute('hidden', '');
    try {
      const response = await fetch(config.cartAddUrl, {
        method: 'POST',
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
        body: new FormData(form)
      });
      if (!response.ok) {
        const result = await response.json().catch(() => ({}));
        throw new Error(result.description || 'cart-add-failed');
      }
      await refresh();
      await open(button);
    } catch {
      const productError = form.querySelector('[data-product-form-error]');
      if (productError) {
        productError.textContent = 'この商品は現在ご購入いただけません。';
        productError.hidden = false;
      }
      setError('この商品は現在ご購入いただけません。');
    } finally {
      setBusy(false);
      if (button) { button.disabled = false; button.textContent = originalText || 'カートに追加'; }
    }
  };

  const addRecommendation = async (variantId, button) => {
    if (inFlight) return;
    const originalText = button.textContent;
    setBusy(true);
    button.disabled = true;
    button.textContent = '追加中…';
    setError('');
    try {
      const response = await fetch(config.cartAddUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({ items: [{ id: Number(variantId), quantity: 1 }] })
      });
      if (!response.ok) throw new Error('cart-add-failed');
      await refresh();
    } catch {
      setError('この商品は現在ご購入いただけません。');
      button.disabled = false;
      button.textContent = originalText;
    } finally {
      setBusy(false);
    }
  };

  document.querySelectorAll('[data-cart-drawer-trigger]').forEach(trigger => trigger.addEventListener('click', event => {
    event.preventDefault();
    open(trigger);
  }));
  drawer.querySelectorAll('[data-cart-drawer-close]').forEach(button => button.addEventListener('click', close));
  drawer.addEventListener('click', event => {
    const recommendationsToggle = event.target.closest('[data-cart-recommendations-toggle]');
    const quantityButton = event.target.closest('[data-cart-quantity]');
    const removeButton = event.target.closest('[data-cart-remove]');
    const recommendationButton = event.target.closest('[data-cart-recommendation-add]');
    const item = event.target.closest('[data-cart-item-key]');
    if (recommendationsToggle && recommendations) {
      const collapsed = recommendations.classList.toggle('is-collapsed');
      recommendationsToggle.setAttribute('aria-expanded', String(!collapsed));
      recommendationsToggle.setAttribute('aria-label', collapsed ? 'おすすめの作品を開く' : 'おすすめの作品を閉じる');
      recommendationsToggle.textContent = collapsed ? '＋' : '−';
    }
    if (quantityButton && item) change(item.dataset.cartItemKey, quantityButton.dataset.cartQuantity);
    if (removeButton && item) change(item.dataset.cartItemKey, 0);
    if (recommendationButton) addRecommendation(recommendationButton.dataset.cartRecommendationAdd, recommendationButton);
  });
  document.addEventListener('keydown', event => { if (event.key === 'Escape' && !drawer.hidden) close(); });
  document.addEventListener('submit', event => {
    const form = event.target.closest?.('[data-product-form]');
    if (!form) return;
    event.preventDefault();
    add(form);
  });

  const openFromCartBridge = () => {
    const url = new URL(window.location.href);
    if (url.searchParams.get('cart') !== 'open') return;
    url.searchParams.delete('cart');
    window.history.replaceState({}, '', `${url.pathname}${url.search}${url.hash}`);
    open();
  };

  openFromCartBridge();
})();
